import { stripLocalePrefix } from "./localized-routes";

export type SessionProfileClaims = {
  boardOrCeoProfile?: boolean;
  displayName?: string;
  email?: string;
  impersonatedByRoleKey?: string;
  impersonatedByUserId?: string;
  impersonatedInvestorId?: string;
  impersonationStartedAt?: string;
  permissions: string[];
  phoneNumber?: string;
  planGates: string[];
  roleKey?: string;
  tenantId?: string;
  userId?: string;
};

const adminWorkspaceAdminRoles = new Set(["global_admin", "tenant_admin", "fund_admin"]);
const adminWorkspaceBillingRoles = new Set(["global_admin", "tenant_admin", "fund_admin"]);
const adminWorkspaceChatRoles = new Set(["global_admin", "tenant_admin", "fund_admin"]);
const adminWorkspaceFilesRoles = new Set(["global_admin", "tenant_admin"]);
const adminWorkspaceForeningFilesRoles = new Set(["global_admin", "tenant_admin", "fund_admin"]);
const adminWorkspaceMarketplaceRoles = new Set(["global_admin", "tenant_admin", "fund_admin"]);
const adminWorkspaceEmployeeRoles = new Set(["fund_employee", "fund_manager"]);
const customerOnlyRoles = new Set(["advisor", "investor"]);
const customerMessagesRoles = new Set(["advisor", "board_member", "ceo", "investor"]);
const customerMarketplaceRoles = new Set(["advisor", "investor"]);
const investorPortalRoles = new Set(["investor", "advisor", "board_member", "ceo"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getOptionalString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];

  return typeof value === "string" && value.trim() ? value : null;
}

function getStringList(record: Record<string, unknown>, key: string): string[] {
  const value = record[key];

  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

export function decodeSessionProfile(rawToken: string | null | undefined): SessionProfileClaims | null {
  const payload = rawToken?.split(".")[1];

  if (!payload) {
    return null;
  }

  try {
    const parsed = JSON.parse(Buffer.from(payload, "base64url").toString("utf8")) as unknown;

    if (!isRecord(parsed)) {
      return null;
    }

    const profile: SessionProfileClaims = {
      permissions: getStringList(parsed, "permissions"),
      planGates: getStringList(parsed, "planGates")
    };
    const displayName = getOptionalString(parsed, "displayName");
    const email = getOptionalString(parsed, "email");
    const impersonatedByRoleKey = getOptionalString(parsed, "impersonatedByRoleKey");
    const impersonatedByUserId = getOptionalString(parsed, "impersonatedByUserId");
    const impersonatedInvestorId = getOptionalString(parsed, "impersonatedInvestorId");
    const impersonationStartedAt = getOptionalString(parsed, "impersonationStartedAt");
    const phoneNumber = getOptionalString(parsed, "phoneNumber");
    const roleKey = getOptionalString(parsed, "roleKey");
    const tenantId = getOptionalString(parsed, "tenantId");
    const userId = getOptionalString(parsed, "userId");

    if (displayName) {
      profile.displayName = displayName;
    }

    if (email) {
      profile.email = email;
    }

    if (impersonatedByRoleKey) {
      profile.impersonatedByRoleKey = impersonatedByRoleKey;
    }

    if (impersonatedByUserId) {
      profile.impersonatedByUserId = impersonatedByUserId;
    }

    if (impersonatedInvestorId) {
      profile.impersonatedInvestorId = impersonatedInvestorId;
    }

    if (impersonationStartedAt) {
      profile.impersonationStartedAt = impersonationStartedAt;
    }

    if (phoneNumber) {
      profile.phoneNumber = phoneNumber;
    }

    if (roleKey) {
      profile.roleKey = roleKey;
    }

    if (tenantId) {
      profile.tenantId = tenantId;
    }

    if (userId) {
      profile.userId = userId;
    }

    if (parsed.boardOrCeoProfile === true) {
      profile.boardOrCeoProfile = true;
    }

    return profile;
  } catch {
    return null;
  }
}

export function isAdminWorkspaceRoute(pathname: string | null | undefined): boolean {
  const normalizedPathname = stripLocalePrefix(pathname);

  return normalizedPathname === "/admin" || normalizedPathname.startsWith("/admin/");
}

export function canAccessAdminWorkspace(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || customerOnlyRoles.has(roleKey)) {
    return false;
  }

  if (adminWorkspaceAdminRoles.has(roleKey)) {
    return true;
  }

  return adminWorkspaceEmployeeRoles.has(roleKey) && profile.permissions.includes("fund.read") && profile.planGates.includes("gate.fund-overview");
}

// A board/ceo profile holds at least one board_member or ceo assignment. Such users may reach the
// admin (fund) workspace via a fund-management assignment, but get a restricted view: no settings,
// no FAQ/appearance management, and they cannot be impersonated. The flag is computed server-side
// at session-mint time (see userHoldsBoardOrCeoRole in the API) and carried as a signed claim.
export function isBoardOrCeoProfile(profile: SessionProfileClaims | null | undefined): boolean {
  return profile?.boardOrCeoProfile === true;
}

// Admin workspace settings (Indstillinger) are hidden and route-blocked for board/ceo profiles.
// Cross-tenant, so Global Admin only - tenant.manage alone would also let a tenant admin in. An
// impersonated session never qualifies; the API enforces the same rule.
export function canAccessAdminUserActivity(profile: SessionProfileClaims | null | undefined): boolean {
  return profile?.roleKey === "global_admin" && !profile.impersonatedByUserId;
}

export function canAccessAdminSettings(profile: SessionProfileClaims | null | undefined): boolean {
  return canAccessAdminWorkspace(profile) && !isBoardOrCeoProfile(profile);
}

// Board/ceo profiles must never impersonate ("Impersonate") or fully log in as a user
// ("Log ind som bruger"), even though they can otherwise reach the forening investor list.
// The action buttons are hidden for them; the API also blocks board/ceo actors server-side.
export function canImpersonateFromAdminWorkspace(profile: SessionProfileClaims | null | undefined): boolean {
  return canAccessAdminWorkspace(profile) && !isBoardOrCeoProfile(profile);
}

export function canAccessInvestorPortal(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey) {
    return false;
  }

  // Only sessions that were authenticated through the investor-portal workspace branch
  // (or which carry an investor-portal-eligible roleKey) should expose the in-app
  // "Skift til investorportal" action. A fund_workspace session (e.g. tenant_admin)
  // would otherwise land on / and see investors[0] from the tenant-wide scope, which
  // is not the user's own data.
  return investorPortalRoles.has(roleKey);
}

export function canAccessAdminBillingWorkspace(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !adminWorkspaceBillingRoles.has(roleKey)) {
    return false;
  }

  if (roleKey === "global_admin" || roleKey === "tenant_admin" || roleKey === "fund_admin") {
    return true;
  }

  return profile.permissions.includes("billing.read") && profile.planGates.includes("gate.billing");
}

export function canAccessAdminChatWorkspace(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !adminWorkspaceChatRoles.has(roleKey)) {
    return false;
  }

  if (roleKey === "global_admin") {
    return true;
  }

  return profile.permissions.includes("communication.read") && profile.planGates.includes("gate.communications");
}

export function canComposeAdminChatWorkspace(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !adminWorkspaceChatRoles.has(roleKey)) {
    return false;
  }

  if (roleKey === "global_admin") {
    return true;
  }

  if (!profile.planGates.includes("gate.communications")) {
    return false;
  }

  return profile.permissions.includes("communication.compose") || profile.permissions.includes("communication.read");
}

export function canManageAdminChatWorkspace(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !adminWorkspaceChatRoles.has(roleKey)) {
    return false;
  }

  if (roleKey === "global_admin") {
    return true;
  }

  return profile.permissions.includes("communication.manage") && profile.planGates.includes("gate.communications");
}

export function canAccessCustomerMessages(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !customerMessagesRoles.has(roleKey)) {
    return false;
  }

  return profile.permissions.includes("communication.read") && profile.planGates.includes("gate.communications");
}

export function canComposeCustomerMessages(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !customerMessagesRoles.has(roleKey)) {
    return false;
  }

  return profile.permissions.includes("communication.compose") && profile.planGates.includes("gate.communications");
}

export function canAccessCustomerMarketplace(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !customerMarketplaceRoles.has(roleKey)) {
    return false;
  }

  return profile.permissions.includes("marketplace.read") && profile.planGates.includes("gate.marketplace");
}

export function canManageAdvisorAccess(profile: SessionProfileClaims | null | undefined): boolean {
  if (!profile?.roleKey) {
    return false;
  }

  return profile.permissions.includes("advisor-access.manage") && profile.planGates.includes("gate.advisor-mandates");
}

export function canAccessAdminMarketplaceWorkspace(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !adminWorkspaceMarketplaceRoles.has(roleKey)) {
    return false;
  }

  if (roleKey === "global_admin") {
    return true;
  }

  return profile.permissions.includes("marketplace.read") && profile.planGates.includes("gate.marketplace");
}

export function canAccessAdminFilesWorkspace(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  return Boolean(profile && roleKey && adminWorkspaceFilesRoles.has(roleKey));
}

export function canManageAdminFilesFundAccess(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  return Boolean(profile && roleKey && adminWorkspaceFilesRoles.has(roleKey));
}

// Dokumenter viser de filforbindelser der er markeret synlige i alle foreninger. Der er ingen
// foreningsafgraensning at haandhaeve, saa adgangen foelger document-room-adgangen alene.
function hasDocumentRoomAccess(profile: SessionProfileClaims | null | undefined): boolean {
  return Boolean(profile?.permissions.includes("document.read") && profile.planGates.includes("gate.document-room"));
}

export function canAccessCustomerDocuments(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !investorPortalRoles.has(roleKey)) {
    return false;
  }

  return hasDocumentRoomAccess(profile);
}

export function canAccessAdminWorkspaceDocuments(profile: SessionProfileClaims | null | undefined): boolean {
  return canAccessAdminWorkspace(profile) && hasDocumentRoomAccess(profile);
}

// FAQ synlig i alle foreninger foelger samme portal-krav som foreningernes egne FAQ, saa enhver
// session der kan se en forenings FAQ ogsaa kan se de globale.
function hasPortalFaqAccess(profile: SessionProfileClaims | null | undefined): boolean {
  return Boolean(profile?.permissions.includes("portal.dashboard.read") && profile.planGates.includes("gate.portal-foundation"));
}

export function canAccessCustomerFaq(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !investorPortalRoles.has(roleKey)) {
    return false;
  }

  return hasPortalFaqAccess(profile);
}

export function canAccessAdminWorkspaceFaq(profile: SessionProfileClaims | null | undefined): boolean {
  return canAccessAdminWorkspace(profile) && canManageAdminForeningFaq(profile);
}

export function canAccessAdminForeningFiles(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  return Boolean(profile && roleKey && adminWorkspaceForeningFilesRoles.has(roleKey));
}

// Production statistics sit behind the Data & BI gate rather than the portal-content one, because
// the numbers come from the same read models the Data & BI module exposes.
export function canAccessAdminForeningProduction(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !adminWorkspaceForeningFilesRoles.has(roleKey)) {
    return false;
  }

  if (roleKey === "global_admin") {
    return true;
  }

  return profile.permissions.includes("data-bi.read") && profile.planGates.includes("gate.data-bi");
}

// Share-value development reads the same NAV and dividend history the forening overview is built
// from, so it sits behind the fund-overview gate rather than the Data & BI one that production uses.
export function canAccessAdminForeningShareValue(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !adminWorkspaceForeningFilesRoles.has(roleKey)) {
    return false;
  }

  if (roleKey === "global_admin") {
    return true;
  }

  return profile.permissions.includes("fund.read") && profile.planGates.includes("gate.fund-overview");
}

export function canManageAdminForeningFaq(profile: SessionProfileClaims | null | undefined): boolean {
  const roleKey = profile?.roleKey;

  if (!profile || !roleKey || !adminWorkspaceForeningFilesRoles.has(roleKey)) {
    return false;
  }

  if (isBoardOrCeoProfile(profile)) {
    return false;
  }

  if (roleKey === "global_admin") {
    return true;
  }

  return profile.permissions.includes("content.manage") && profile.planGates.includes("gate.portal-content");
}